import React, { useEffect, useState, useCallback } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { Loader2, TrendingUp, Users, Coins, ShoppingCart, RefreshCw, Receipt } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RANGES = [
  { id: 'daily',   label: '30 days',   xKey: 'label' },
  { id: 'weekly',  label: '12 weeks',  xKey: 'label' },
  { id: 'monthly', label: '12 months', xKey: 'label' },
  { id: 'yearly',  label: 'All years', xKey: 'label' },
];

/**
 * Admin EVC analytics — tiles (all-time / 30d / 365d / unique customers)
 * + per-period stacked-ish bar chart (Credits sold + €Spent overlay) with a
 * range tab toggle. Used inside EVCEditor's "Analytics" tab AND in the
 * existing global AnalyticsPanel.
 */
export default function EVCAnalytics({ token, compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('monthly');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API_URL}/api/evc/admin/analytics`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok && !cancelled) setData(await r.json());
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 sm:p-6" data-testid="evc-analytics">
        <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading EVC analytics…</div>
      </section>
    );
  }
  if (!data) return null;

  const s = data.summary;
  const series = data[range] || [];
  // Format X-axis labels per range — daily shows MM-DD, weekly shows W##, etc.
  const fmtX = (v) => {
    if (range === 'daily')   return v?.slice(5);            // MM-DD
    if (range === 'weekly')  return v?.split('-W')[1] || v; // W##
    if (range === 'monthly') return v?.slice(2);            // YY-MM
    return v;                                               // YYYY
  };

  return (
    <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 sm:p-6" data-testid="evc-analytics">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-semibold text-slate-800 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-amber-600" /> EVC Sales Analytics
        </h2>
        <div className="inline-flex bg-slate-100 rounded-lg p-1 text-xs" data-testid="evc-analytics-range">
          {RANGES.map((r) => (
            <button
              key={r.id}
              onClick={() => setRange(r.id)}
              data-testid={`evc-analytics-range-${r.id}`}
              className={`px-2.5 py-1 rounded font-bold transition-all ${range === r.id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >{r.label}</button>
          ))}
        </div>
      </div>

      {/* Summary tiles */}
      <div className={`grid ${compact ? 'grid-cols-2 lg:grid-cols-5' : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5'} gap-2 sm:gap-3 mb-4`}>
        <Tile label="All-time credits"   big={s.all_time.credits}   sub={`${s.all_time.purchases} buys · €${s.all_time.spent.toFixed(0)}`} icon={<Coins className="w-4 h-4" />} color="amber"  testid="evc-tile-alltime" />
        <Tile label="Credits last 30d"   big={s.last_30d.credits}   sub={`${s.last_30d.purchases} buys · €${s.last_30d.spent.toFixed(0)}`}  icon={<ShoppingCart className="w-4 h-4" />} color="emerald" testid="evc-tile-30d" />
        <Tile label="Credits last 12mo"  big={s.last_365d.credits}  sub={`${s.last_365d.purchases} buys · €${s.last_365d.spent.toFixed(0)}`} icon={<TrendingUp className="w-4 h-4" />} color="cyan"   testid="evc-tile-365d" />
        <Tile label="Customers all-time" big={s.unique_customers_all} sub="bought ≥ 1 pack" icon={<Users className="w-4 h-4" />} color="violet" testid="evc-tile-cust-all" />
        <Tile label="Customers (30d)"    big={s.unique_customers_30d} sub="active this month" icon={<Users className="w-4 h-4" />} color="rose"  testid="evc-tile-cust-30d" />
      </div>

      {/* Chart */}
      <div className="h-56 sm:h-64 w-full" data-testid={`evc-analytics-chart-${range}`}>
        {series.length === 0 ? (
          <div className="text-sm text-slate-400 text-center py-12">No EVC sales in this range yet.</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={series} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="period" tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={fmtX} interval="preserveStartEnd" />
              <YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#64748b' }} allowDecimals={false} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: '#10b981' }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e2e8f0' }}
                formatter={(v, name) => name === 'spent' ? [`€${Number(v).toFixed(2)}`, 'Revenue'] : [v, name === 'credits' ? 'Credits' : name === 'purchases' ? 'Purchases' : name]}
                labelFormatter={(l) => `Period: ${l}`}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar yAxisId="left"  dataKey="credits"   name="Credits"  fill="#f59e0b" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="right" dataKey="spent"     name="Revenue €" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Real-money sales history — computed from the `transactions`
          collection (matches every EVC-tagged line in the Transactions
          page) so the numbers represent ACTUAL paid revenue. Mirrors
          the EVC.de reseller-stat page format (Month / Sales) and adds
          our own Revenue + Credits columns. */}
      <SalesHistoryBlock token={token} />
    </section>
  );
}

/** Real-money EVC sales history — own fetch + Refresh button, fully
    independent from the daily/weekly/monthly tabs above. Listens for
    `window` "evc-purchase-completed" events so it auto-refreshes when a
    customer/admin completes a purchase elsewhere on the page. */
function SalesHistoryBlock({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await fetch(`${API_URL}/api/evc/admin/sales-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setData(await r.json());
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
    // Auto-refresh after any EVC purchase completes elsewhere in the app.
    // EVCEditor dispatches this event in its purchase handler so admins
    // never need to manually hit Refresh after crediting a customer.
    const onPurchase = () => load();
    window.addEventListener('evc-purchase-completed', onPurchase);
    return () => window.removeEventListener('evc-purchase-completed', onPurchase);
  }, [load]);

  if (loading) {
    return (
      <div className="mt-6 pt-6 border-t border-slate-200 text-sm text-slate-500 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading real-money sales history…
      </div>
    );
  }
  if (!data) return null;

  const monthly = data.monthly || [];
  const totals  = data.totals  || { sales_count: 0, revenue_eur: 0, credits_sold: 0 };
  const fmtX = (v) => v?.split('_').join('-')?.slice(2);   // YY-MM
  const fmtMoney = (n) => `€${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  return (
    <div className="mt-6 pt-6 border-t border-slate-200" data-testid="evc-sales-history-block">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2 text-sm">
          <Receipt className="w-4 h-4 text-emerald-600" /> EVC credits sales history
          <span className="text-[11px] font-normal text-slate-500" title="Only EVC credit sales (money customers paid us for WinOLS/EVC credits). Wallet top-ups live under Recharges on the Analytics page.">— EVC credit sales only, from Transactions</span>
        </h3>
        <div className="flex items-center gap-3 text-xs">
          {data.synced_at && (
            <span className="text-slate-400">
              Last refreshed {new Date(data.synced_at).toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={load}
            disabled={refreshing}
            className="px-2.5 py-1 border border-slate-300 rounded-lg flex items-center gap-1.5 hover:bg-slate-50 disabled:opacity-50 font-medium"
            data-testid="evc-sales-history-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        <MiniStat label="Total sales"  value={totals.sales_count} testid="evc-history-total-sales" />
        <MiniStat label="Total revenue" value={fmtMoney(totals.revenue_eur)} accent="emerald" testid="evc-history-total-revenue" />
        <MiniStat label="Credits sold" value={totals.credits_sold} testid="evc-history-total-credits" />
        <MiniStat label="Active months" value={totals.months_with_sales} testid="evc-history-active-months" />
      </div>

      {/* Bar chart — EVC.de uses just bars, we add a Revenue overlay too */}
      <div className="h-52 w-full mb-3" data-testid="evc-sales-history-chart">
        {monthly.length === 0 ? (
          <div className="text-sm text-slate-400 text-center py-10">No EVC transactions yet.</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthly} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={fmtX} interval="preserveStartEnd" />
              <YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#64748b' }} allowDecimals={false} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: '#10b981' }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e2e8f0' }}
                formatter={(v, name) => name === 'Revenue €' ? [`€${Number(v).toLocaleString()}`, 'Revenue'] : [v, name]}
                labelFormatter={(l) => `Month: ${l?.replace('_', '-')}`}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar yAxisId="left"  dataKey="sales_count" name="Sales" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="right" dataKey="revenue_eur" name="Revenue €" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Table — mirrors EVC.de's reseller-stat layout (Month / Sales)
          plus revenue and credits columns we derive from our data. */}
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-sm" data-testid="evc-sales-history-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 font-bold">
            <tr>
              <th className="px-3 py-2 text-left whitespace-nowrap">Month</th>
              <th className="px-3 py-2 text-right whitespace-nowrap">Sales</th>
              <th className="px-3 py-2 text-right whitespace-nowrap">Revenue</th>
              <th className="px-3 py-2 text-right whitespace-nowrap">Credits sold</th>
            </tr>
          </thead>
          <tbody>
            {monthly.length === 0 ? (
              <tr><td colSpan={4} className="text-center py-6 text-slate-400">No EVC transactions yet.</td></tr>
            ) : monthly.map((row) => (
              <tr key={row.month} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`evc-history-row-${row.month}`}>
                <td className="px-3 py-2 font-mono text-slate-700 whitespace-nowrap">{row.month}</td>
                <td className="px-3 py-2 text-right font-bold tabular-nums whitespace-nowrap">{row.sales_count}</td>
                <td className="px-3 py-2 text-right font-semibold text-emerald-700 tabular-nums whitespace-nowrap">{fmtMoney(row.revenue_eur)}</td>
                <td className="px-3 py-2 text-right text-slate-600 tabular-nums whitespace-nowrap">{row.credits_sold || <span className="text-slate-300">—</span>}</td>
              </tr>
            ))}
          </tbody>
          {monthly.length > 0 && (
            <tfoot className="bg-slate-50 font-bold text-slate-800">
              <tr className="border-t border-slate-200">
                <td className="px-3 py-2 text-left whitespace-nowrap">Total</td>
                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{totals.sales_count}</td>
                <td className="px-3 py-2 text-right text-emerald-700 tabular-nums whitespace-nowrap">{fmtMoney(totals.revenue_eur)}</td>
                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{totals.credits_sold || '—'}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function MiniStat({ label, value, accent, testid }) {
  return (
    <div className="px-3 py-2 rounded-lg bg-slate-50 border border-slate-200" data-testid={testid}>
      <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500">{label}</div>
      <div className={`text-base font-bold tabular-nums ${accent === 'emerald' ? 'text-emerald-700' : 'text-slate-800'}`}>{value}</div>
    </div>
  );
}

function Tile({ label, big, sub, icon, color, testid }) {
  const palettes = {
    amber:   'from-amber-50 to-orange-50 border-amber-200 text-amber-700',
    emerald: 'from-emerald-50 to-teal-50 border-emerald-200 text-emerald-700',
    cyan:    'from-cyan-50 to-blue-50 border-cyan-200 text-cyan-700',
    violet:  'from-violet-50 to-fuchsia-50 border-violet-200 text-violet-700',
    rose:    'from-rose-50 to-pink-50 border-rose-200 text-rose-700',
  };
  return (
    <div className={`rounded-xl border bg-gradient-to-br p-3 ${palettes[color]}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider opacity-80">
        {icon}
        <span className="truncate">{label}</span>
      </div>
      <div className="mt-1 text-xl sm:text-2xl font-extrabold leading-none tabular-nums">{big}</div>
      <div className="text-[10px] sm:text-xs text-slate-500 mt-1 truncate">{sub}</div>
    </div>
  );
}
